// ──────────────────────────────────────────────
// SessionManifest — IndexedDB CRUD for session metadata.
//
// Stored in a separate IndexedDB database from chunk storage
// because manifests need transactional updates for array fields
// (uploadedChunks, failedChunks) and must survive OPFS issues.
//
// See ADR-007 for design rationale.
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';
import type { DBSchema, IDBPDatabase } from 'idb';
import { openDB } from 'idb';

import type { SessionManifestData } from './types';

const DB_NAME = 'JotPsychSessionManifests';
const DB_VERSION = 1;
const STORE_NAME = 'manifests' as const;

interface ManifestDB extends DBSchema {
  manifests: {
    key: string; // sessionId
    value: SessionManifestData;
  };
}

/**
 * Persistent storage for session manifests using IndexedDB.
 *
 * Each manifest tracks per-session metadata: which chunks were produced,
 * uploaded, failed, and whether recording-end was called/confirmed.
 */
export class SessionManifest {
  private db: IDBPDatabase<ManifestDB> | null = null;

  async open(): Promise<void> {
    if (this.db) return;

    this.db = await openDB<ManifestDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
        }
      },
    });
  }

  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  /** Create or overwrite a manifest. */
  async put(manifest: SessionManifestData): Promise<void> {
    const db = this.ensureOpen();
    const tx = db.transaction(STORE_NAME, 'readwrite', { durability: 'relaxed' });
    await tx.store.put(manifest);
    await tx.done;
  }

  /** Read a manifest by sessionId, or null if not found. */
  async get(sessionId: string): Promise<SessionManifestData | null> {
    const db = this.ensureOpen();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const result = await tx.store.get(sessionId);
    await tx.done;
    return result ?? null;
  }

  /** Delete a manifest by sessionId. No-op if not found. */
  async delete(sessionId: string): Promise<void> {
    const db = this.ensureOpen();
    const tx = db.transaction(STORE_NAME, 'readwrite', { durability: 'relaxed' });
    await tx.store.delete(sessionId);
    await tx.done;
  }

  /** Return all stored manifests. */
  async listAll(): Promise<SessionManifestData[]> {
    const db = this.ensureOpen();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const all = await tx.store.getAll();
    await tx.done;
    return all;
  }

  /**
   * Atomically update specific fields of a manifest.
   * Reads current value, applies updater, writes back in a single transaction.
   * Returns the updated manifest, or null if the session doesn't exist.
   */
  async update(
    sessionId: string,
    updater: (current: SessionManifestData) => SessionManifestData,
  ): Promise<SessionManifestData | null> {
    const db = this.ensureOpen();
    const tx = db.transaction(STORE_NAME, 'readwrite', { durability: 'relaxed' });
    const current = await tx.store.get(sessionId);

    if (!current) {
      await tx.done;
      return null;
    }

    const updated = updater(current);
    await tx.store.put(updated);
    await tx.done;
    return updated;
  }

  /** Create a new manifest with default values. */
  async create(sessionId: string, mimeType: string, encounterId?: string): Promise<SessionManifestData> {
    const manifest: SessionManifestData = {
      sessionId,
      encounterId,
      createdAt: new Date().toISOString(),
      mimeType,
      totalChunks: 0,
      uploadedChunks: [],
      failedChunks: [],
      recordingEndCalled: false,
      recordingEndConfirmed: false,
      initSegmentPersisted: false,
    };

    await this.put(manifest);

    SentryService.addBreadcrumb({
      category: 'session.manifest',
      message: `Created manifest for session ${sessionId}`,
      level: 'info',
    });

    return manifest;
  }

  // ── Convenience update methods ──

  async incrementTotalChunks(sessionId: string): Promise<void> {
    await this.update(sessionId, m => ({
      ...m,
      totalChunks: m.totalChunks + 1,
    }));
  }

  async markChunkUploaded(sessionId: string, chunkIndex: number): Promise<void> {
    await this.update(sessionId, m => ({
      ...m,
      uploadedChunks: m.uploadedChunks.includes(chunkIndex)
        ? m.uploadedChunks
        : [...m.uploadedChunks, chunkIndex],
    }));
  }

  async markChunkFailed(sessionId: string, chunkIndex: number): Promise<void> {
    await this.update(sessionId, m => ({
      ...m,
      failedChunks: m.failedChunks.includes(chunkIndex)
        ? m.failedChunks
        : [...m.failedChunks, chunkIndex],
    }));
  }

  async markRecordingEndCalled(sessionId: string): Promise<void> {
    await this.update(sessionId, m => ({
      ...m,
      recordingEndCalled: true,
    }));
  }

  async markRecordingEndConfirmed(sessionId: string): Promise<void> {
    await this.update(sessionId, m => ({
      ...m,
      recordingEndCalled: true,
      recordingEndConfirmed: true,
    }));
  }

  async markInitSegmentPersisted(sessionId: string): Promise<void> {
    await this.update(sessionId, m => ({
      ...m,
      initSegmentPersisted: true,
    }));
  }

  // ── Private ──

  private ensureOpen(): IDBPDatabase<ManifestDB> {
    if (!this.db) {
      throw new Error('SessionManifest not opened — call open() first');
    }
    return this.db;
  }
}
